import { useMemo, useState } from 'react'
import { ChartViewToggle, ShareDonut, foldSlices } from '@/components/shared/charts/ShareCharts'
import { AiAssistantsCard, LLMLogo } from './AiAssistantsCard'
import { ReportPagination, ReportSearch } from './AnalyticsReport'
import type { LLMReferralRow } from './llmReferrals'

type AssistantsView = 'table' | 'donut'
const ASSISTANTS_VIEW_OPTIONS = [
  { value: 'table' as const, label: 'Table', icon: 'table' as const },
  { value: 'donut' as const, label: 'Donut', icon: 'donut' as const },
]

const PREVIEW_ROWS = 5
const PAGE_SIZE = 10

/**
 * AI assistants on Traffic sources (was the LLM Referrals tab): the top five
 * beside the channel table, expanding in place to the full list with search,
 * pagination and the donut view.
 */
export function AiAssistantsPanel({ rows, totalVisits }: { rows: LLMReferralRow[]; totalVisits: number }) {
  const [expanded, setExpanded] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [view, setView] = useState<AssistantsView>('table')

  const filtered = useMemo(() => {
    if (!expanded || !searchTerm) return rows
    const q = searchTerm.toLowerCase()
    return rows.filter((item) => item.name.toLowerCase().includes(q) || item.domain.toLowerCase().includes(q))
  }, [rows, searchTerm, expanded])

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const shown = expanded ? filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE) : rows

  const collapse = () => {
    setExpanded(false)
    setSearchTerm('')
    setCurrentPage(1)
  }

  return (
    <AiAssistantsCard
      rows={shown}
      totalVisits={totalVisits}
      limit={expanded ? undefined : PREVIEW_ROWS}
      emptyMessage={searchTerm ? 'No assistants match your search.' : undefined}
      // Preview keeps Paper's header total; the full list adds search and the
      // donut view in its place.
      headerAction={
        expanded ? (
          <div className="flex items-center gap-2.5">
            <ReportSearch
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setCurrentPage(1)
              }}
              placeholder="Search assistants"
              className="sm:w-[160px]"
            />
            <ChartViewToggle options={ASSISTANTS_VIEW_OPTIONS} value={view} onChange={setView} />
          </div>
        ) : undefined
      }
      alternateBody={
        // Assistant visits add up, so shares are of all assistant visits (the
        // table's denominator); a search leaves the rest of the ring empty.
        expanded && view === 'donut' && filtered.length > 0 ? (
          <ShareDonut
            slices={foldSlices(
              filtered.map((item) => ({
                key: item.name,
                label: item.name,
                value: item.count,
                share: item.percentage,
                leading: <LLMLogo logo={item.logo} color={item.color} size={16} />,
              })),
            )}
            total={totalVisits}
            totalLabel="assistant visits"
          />
        ) : undefined
      }
      footer={
        expanded && view === 'table' && totalPages > 1 ? (
          <ReportPagination
            from={(currentPage - 1) * PAGE_SIZE + 1}
            to={Math.min(currentPage * PAGE_SIZE, filtered.length)}
            total={filtered.length}
            noun="assistants"
            onPrevious={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            previousDisabled={currentPage === 1}
            nextDisabled={currentPage === totalPages}
          />
        ) : undefined
      }
      footerLabel={
        expanded
          ? view === 'table' && totalPages <= 1 ? 'Show fewer' : undefined
          : rows.length > PREVIEW_ROWS ? `View all ${rows.length} assistants` : undefined
      }
      onFooterClick={expanded ? collapse : () => setExpanded(true)}
    />
  )
}
